import { useCallback } from "react";
import type { ObjectAssetInfo } from "../api";
import { useDragPreview } from "../hooks/useDragPreview";
import { useModelsCatalog } from "../hooks/useModelsCatalog";
import { ModelGrid } from "./modelsTab/ModelGrid";
import { ModelSearchBar } from "./modelsTab/ModelSearchBar";
import { ModelStatus } from "./modelsTab/ModelStatus";
import { ModelTagChips } from "./modelsTab/ModelTagChips";

/**
 * Models tab — search, tag chips, and a compact drag-to-place grid.
 */
export function ModelsTab() {
  const { setDragPreview } = useDragPreview();
  const {
    models,
    loading,
    error,
    fetchModels,
    searchQuery,
    setSearchQuery,
    tag,
    setTag,
    availableTags,
    tagCounts,
    filteredModels,
    resultSourceCount,
  } = useModelsCatalog();

  const handleDragStart = useCallback(
    (model: ObjectAssetInfo) => {
      setDragPreview({ objectId: model.id, position: null });
    },
    [setDragPreview],
  );

  const isFiltered = Boolean(searchQuery) || tag !== "all";

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 px-3 pt-2 pb-2 border-b border-sands-100">
        <ModelSearchBar value={searchQuery} onChange={setSearchQuery} />
        <ModelTagChips
          tag={tag}
          onTagChange={setTag}
          totalCount={models.length}
          availableTags={availableTags}
          tagCounts={tagCounts}
          resultCount={filteredModels.length}
          resultSourceCount={resultSourceCount}
        />
      </div>

      <div className="p-1 flex-1 min-h-0 overflow-hidden">
        {loading && <ModelStatus kind="loading" />}
        {error && (
          <ModelStatus kind="error" message={error} onRetry={fetchModels} />
        )}
        {!loading && !error && filteredModels.length === 0 && (
          <ModelStatus kind="empty" filtered={isFiltered} />
        )}
        {!loading && !error && filteredModels.length > 0 && (
          <ModelGrid models={filteredModels} onDragStart={handleDragStart} />
        )}
      </div>
    </div>
  );
}
